import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { Plane, LayoutDashboard, Bot, LineChart, Target, LogOut, Send, Loader2, Zap, Sparkles, Upload, Users, FileText, Flame, Award, CalendarDays, CheckCircle2, AlertTriangle, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API, api } from "@/lib/api";
import { TODAY_PLAN, TRAJECTORY, PERF_METRICS } from "@/data/mock";
import { track, EVENTS } from "@/lib/analytics";
import { logger } from "@/lib/logger";
import { LearnTab } from "@/components/LearnTab";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "learn", label: "Learn", icon: BookOpen },
  { id: "coach", label: "AI Coach", icon: Bot },
  { id: "mocks", label: "Mock Analysis", icon: Upload },
  { id: "performance", label: "Performance", icon: LineChart },
  { id: "parent", label: "Parent View", icon: Users },
];

// Stable chart configs (avoid new object refs on every render)
const CHART_MARGIN = { top: 5, right: 5, left: -25, bottom: 0 };
const AXIS_TICK = { fontSize: 10 };
const SCORE_DOMAIN = [100, 200];
const CHART_DOT = { r: 3 };

const ChartTip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs">
      <p className="text-muted-foreground">{label}</p>
      <p className="font-semibold text-primary">{payload[0].value} marks</p>
    </div>
  ) : null;

const Coach = ({ user }) => {
  const [messages, setMessages] = useState([
    { id: "m0", role: "ai", text: `Hi ${user?.name?.split(" ")[0] || "there"} — I'm your sarkarijobai coach. Ask me what to focus on today, or how to use your available time.` },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const idRef = useRef(1);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const aiId = `m${idRef.current++}`;
    setMessages((m) => [...m, { id: `m${idRef.current++}`, role: "user", text }, { id: aiId, role: "ai", text: "" }]);
    setStreaming(true);
    track(EVENTS.FIRST_STUDY_PLAN, {});

    try {
      const res = await fetch(`${API}/coach/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, text: last.text + chunk };
          return copy;
        });
      }
    } catch (err) {
      logger.error("Coach stream failed:", err?.message);
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        copy[copy.length - 1] = { ...last, text: "The coach is briefly unavailable. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const suggestions = ["I only have 2 hours today. What should I do?", "What's costing me the most marks?", "Plan my next 3 days."];

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] rounded-2xl border border-border bg-card overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center">
          <Bot className="h-4 w-4 text-white" />
        </div>
        <div>
          <p className="text-sm font-semibold">sarkarijobai Coach</p>
          <p className="text-[11px] text-muted-foreground">Powered by your preparation data</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 no-scrollbar">
        {messages.map((m, i) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"
            }`}>
              {m.text || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
            </div>
          </div>
        ))}
      </div>

      {messages.length <= 1 && (
        <div className="px-5 pb-2 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button key={s} onClick={() => setInput(s)} className="text-xs rounded-full border border-border px-3 py-1.5 text-muted-foreground hover:border-primary/40 transition-colors" data-testid="coach-suggestion">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="p-4 border-t border-border flex items-center gap-2">
        <input
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask your coach anything…"
          className="flex-1 rounded-full border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          data-testid="coach-input"
        />
        <button onClick={send} disabled={streaming}
          className="h-10 w-10 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white flex items-center justify-center disabled:opacity-50"
          data-testid="coach-send">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

const Overview = ({ user }) => {
  const p = user?.profile || {};
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-amber-400/25 bg-gradient-to-r from-amber-500/10 to-red-600/5 p-6">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">Welcome to sarkarijobai. Your preparation starts now.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Preparing for <span className="font-medium text-foreground">{p.exam || "your exam"}</span> · Target{" "}
              <span className="font-medium text-foreground">{p.target || "—"}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-5">
            <Zap className="h-4 w-4 text-primary" />
            <h3 className="font-display text-sm font-bold uppercase tracking-wide">Today's highest-impact plan</h3>
          </div>
          <div className="space-y-2.5">
            {TODAY_PLAN.map((t) => (
              <div key={t.n} className="flex items-center gap-3 rounded-xl border border-border px-4 py-3 hover:border-primary/40 transition-colors">
                <span className="font-display text-xs font-bold text-muted-foreground w-5">{t.n}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium">{t.topic}</p>
                  <p className="text-[11px] text-muted-foreground">{t.time}</p>
                </div>
                {t.impact && (
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${t.impact === "HIGH IMPACT" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {t.impact}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-5">Score trajectory</h3>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={TRAJECTORY} margin={CHART_MARGIN}>
                <defs>
                  <linearGradient id="dashFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis domain={SCORE_DOMAIN} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#dashFill)" dot={CHART_DOT} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Estimates based on available data — not a guarantee of outcomes.</p>
        </div>
      </div>
    </div>
  );
};

const Performance = () => (
  <div className="grid md:grid-cols-2 gap-6">
    <div className="rounded-2xl border border-border bg-card p-6">
      <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-6">Skill breakdown</h3>
      <div className="space-y-5">
        {PERF_METRICS.map((m) => (
          <div key={m.label}>
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">{m.label}</span>
              <span className="text-primary font-medium">{m.value}{m.suffix}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-red-500" style={{ width: `${m.value}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="rounded-2xl border border-border bg-card p-6">
      <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-5">Key metrics</h3>
      <div className="grid grid-cols-2 gap-4">
        {[["Current score", "142/300"], ["Target", "180+"], ["Est. percentile", "96.8"], ["Biggest opportunity", "Maths"]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-border p-4">
            <p className="text-[11px] text-muted-foreground">{l}</p>
            <p className="font-display text-xl font-bold mt-1">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const MockAnalysis = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/mock/analyses");
      setAnalyses(data.analyses || []);
    } catch (err) {
      logger.error("Failed to load mock analyses:", err?.message);
    }
  };
  // Runs once on mount to load existing analyses.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setLoading(true);
    track(EVENTS.FIRST_MOCK_UPLOADED, { source: "dashboard" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/mock/analyze`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Analysis failed");
      }
      await load();
    } catch (err) {
      setError(err.message || "Could not analyse the report. Please try another file.");
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-2">Upload a mock report</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Upload a screenshot or PDF of your latest mock result. The AI reads your score and weak topics automatically.
        </p>
        <input ref={inputRef} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid="mock-file-input" />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white font-medium px-6 py-3 hover:scale-[1.02] transition-transform disabled:opacity-60"
          data-testid="mock-upload-button"
        >
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analysing report…</> : <><Upload className="h-4 w-4" /> Upload mock report</>}
        </button>
        {error && <p className="mt-3 text-sm text-destructive" data-testid="mock-error">{error}</p>}
        <p className="mt-3 text-[11px] text-muted-foreground">Supported: PNG, JPG, PDF · Max 10MB. The AI only reads what's visible in your report.</p>
      </div>

      {analyses.length === 0 && !loading && (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center">
          <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No mock reports analysed yet. Upload your first one above.</p>
        </div>
      )}

      <div className="space-y-4" data-testid="mock-analyses-list">
        {analyses.map((a) => (
          <div key={a.analysis_id} className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium truncate max-w-[220px]">{a.file_name || "Mock report"}</span>
              </div>
              <span className="text-[11px] text-muted-foreground">{(a.created_at || "").slice(0, 10)}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[["Score", a.score != null ? `${a.score}${a.max_score ? "/" + a.max_score : ""}` : "—"],
                ["Percentile", a.percentile != null ? a.percentile : "—"],
                ["Accuracy", a.accuracy != null ? `${a.accuracy}%` : "—"],
                ["Weak areas", (a.weak_topics || []).length]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-border p-3">
                  <p className="text-[11px] text-muted-foreground">{l}</p>
                  <p className="font-display text-lg font-bold">{v}</p>
                </div>
              ))}
            </div>
            {a.summary && <p className="text-sm text-muted-foreground mb-4">{a.summary}</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              {a.weak_topics?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-destructive mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Weak topics</p>
                  <div className="flex flex-wrap gap-1.5">
                    {a.weak_topics.map((t) => <span key={t} className="text-xs rounded-full bg-destructive/10 text-destructive px-2.5 py-1">{t}</span>)}
                  </div>
                </div>
              )}
              {a.strong_topics?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-2 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Strong topics</p>
                  <div className="flex flex-wrap gap-1.5">
                    {a.strong_topics.map((t) => <span key={t} className="text-xs rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2.5 py-1">{t}</span>)}
                  </div>
                </div>
              )}
            </div>
            {a.recommendations?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs font-semibold text-primary mb-2">AI recommendations</p>
                <ul className="space-y-1.5">
                  {a.recommendations.map((r) => <li key={r} className="text-sm text-muted-foreground flex gap-2"><span className="text-primary">→</span>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ParentView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/parent/summary");
        setData(res.data);
      } catch (err) {
        logger.error("Failed to load parent summary:", err?.message);
      } finally {
        setLoading(false);
      }
    })();
    // Runs once on mount to load the parent summary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!data) return null;

  const stats = [
    { label: "Active study days", value: data.active_days, icon: Flame },
    { label: "Coach interactions", value: data.coach_interactions, icon: Bot },
    { label: "Mocks tracked", value: data.mocks_tracked, icon: FileText },
    { label: "Latest mock", value: data.latest_mock_score || "—", icon: Award },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-amber-400/25 bg-gradient-to-r from-amber-500/10 to-red-600/5 p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-2">Parent view</p>
        <h3 className="font-display text-xl font-bold">{data.student_name || "Your child"}'s preparation</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Preparing for <span className="font-medium text-foreground">{data.exam || "—"}</span> · Target{" "}
          <span className="font-medium text-foreground">{data.target || "—"}</span>
          {data.exam_date ? <> · Exam on <span className="font-medium text-foreground">{data.exam_date}</span></> : null}
        </p>
        <p className="text-xs text-muted-foreground mt-2">A simple, honest view of consistency and progress — no scores are inflated.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-2xl border border-border bg-card p-5">
            <s.icon className="h-4 w-4 text-primary mb-3" />
            <p className="font-display text-2xl font-bold">{s.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-5">Score trajectory</h3>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={TRAJECTORY} margin={CHART_MARGIN}>
                <defs>
                  <linearGradient id="parentFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis domain={SCORE_DOMAIN} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#parentFill)" dot={CHART_DOT} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Estimates based on available data — not a guarantee of outcomes.</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-4">Focus areas</h3>
          {data.weak_topics?.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {data.weak_topics.map((t) => <span key={t} className="text-xs rounded-full bg-destructive/10 text-destructive px-2.5 py-1">{t}</span>)}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Weak areas will appear here once a mock report is analysed.</p>
          )}
          <div className="mt-6 pt-4 border-t border-border space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Preparation level</span><span className="font-medium">{data.level || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Daily study</span><span className="font-medium">{data.daily_hours || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span className="font-medium capitalize">{data.plan || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Member since</span><span className="font-medium">{data.member_since || "—"}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-border p-4 shrink-0">
        <div className="flex items-center gap-2 px-2 mb-8">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center">
            <Plane className="h-4 w-4 text-white -rotate-45" />
          </div>
          <span className="font-display text-lg font-bold">sarkarijobai</span>
        </div>
        <nav className="space-y-1 flex-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                tab === n.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"
              }`} data-testid={`dashboard-nav-${n.id}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <button onClick={doLogout} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted" data-testid="dashboard-logout">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </aside>

      <div className="flex-1 min-w-0">
        {/* mobile top nav */}
        <div className="md:hidden flex items-center gap-1 border-b border-border p-2 overflow-x-auto no-scrollbar">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium whitespace-nowrap ${tab === n.id ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}>
              <n.icon className="h-3.5 w-3.5" /> {n.label}
            </button>
          ))}
        </div>

        <div className="p-5 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight">
                {tab === "overview" && "Your dashboard"}
                {tab === "learn" && "Learn"}
                {tab === "coach" && "AI Coach"}
                {tab === "mocks" && "Mock Analysis"}
                {tab === "performance" && "Performance"}
                {tab === "parent" && "Parent View"}
              </h1>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            <button onClick={doLogout} className="md:hidden text-sm text-muted-foreground">Log out</button>
          </div>

          {tab === "overview" && <Overview user={user} />}
          {tab === "learn" && <LearnTab />}
          {tab === "coach" && <Coach user={user} />}
          {tab === "mocks" && <MockAnalysis />}
          {tab === "performance" && <Performance />}
          {tab === "parent" && <ParentView />}
        </div>
      </div>
    </div>
  );
}
